import axios from 'axios';
import api from './api';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const CHECKIN_SESSION_KEY = 'checkin_portal_session';

const publicApi = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

export interface CheckInStats {
  eventId: string;
  eventTitle: string;
  eventSlug?: string;
  totalRegistrations: number;
  checkedInCount: number;
  pendingCheckIn: number;
  checkInPercentage: string;
  recentCheckIns?: Array<{ name?: string; ticketType?: string; checkedInAt?: string }>;
}

export interface CheckInParticipant {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  ticketName?: string;
  checkedIn: boolean;
  checkedInAt?: string;
  confirmationCode?: string;
  checkInCode?: string;
}

export interface CheckInScanResult {
  success: boolean;
  message: string;
  error?: string;
  data?: Record<string, unknown>;
}

export interface CheckInPortalStatus {
  status: 'none' | 'active' | 'revoked' | 'expired';
  hasPortal: boolean;
  createdAt?: string;
  expiresAt?: string;
  revokedAt?: string;
  stats?: CheckInStats;
}

export function saveCheckInSession(token: string): void {
  sessionStorage.setItem(CHECKIN_SESSION_KEY, token);
}

export function getCheckInSession(): string | null {
  return sessionStorage.getItem(CHECKIN_SESSION_KEY);
}

export function clearCheckInSession(): void {
  sessionStorage.removeItem(CHECKIN_SESSION_KEY);
}

function sessionHeaders(): Record<string, string> {
  const session = getCheckInSession();
  return session ? { 'X-Checkin-Session': session } : {};
}

export async function bootstrapCheckInPortal(token: string) {
  const res = await publicApi.get('/public/check-in/bootstrap', { params: { t: token } });
  return res.data;
}

export async function verifyCheckInPassword(token: string, password: string) {
  const res = await publicApi.post('/public/check-in/verify-password', { token, password });
  return res.data;
}

export async function fetchCheckInStats(): Promise<CheckInStats> {
  const res = await publicApi.get('/public/check-in/stats', { headers: sessionHeaders() });
  return res.data.data;
}

export async function fetchCheckInParticipants(params: {
  search?: string;
  filter?: 'all' | 'checked' | 'pending';
  limit?: number;
}) {
  const res = await publicApi.get('/public/check-in/participants', {
    params,
    headers: sessionHeaders(),
  });
  return res.data.data as { participants: CheckInParticipant[]; total: number };
}

export async function scanCheckInCode(code: string): Promise<CheckInScanResult> {
  const res = await publicApi.post('/public/check-in/scan', { code }, { headers: sessionHeaders() });
  return res.data;
}

export async function manualCheckIn(registrationId: string): Promise<CheckInScanResult> {
  const res = await publicApi.post(
    `/public/check-in/${registrationId}/checkin`,
    {},
    { headers: sessionHeaders() }
  );
  return res.data;
}

export async function undoCheckIn(registrationId: string): Promise<CheckInScanResult> {
  const res = await publicApi.post(
    `/public/check-in/${registrationId}/uncheckin`,
    {},
    { headers: sessionHeaders() }
  );
  return res.data;
}

export async function postCheckInClientLogs(payload: {
  sessionId: string;
  eventId?: string;
  entries: Array<{
    ts: string;
    level: string;
    event: string;
    message: string;
    data?: Record<string, unknown>;
    durationMs?: number;
  }>;
  device?: Record<string, unknown>;
}) {
  const res = await publicApi.post('/public/check-in/client-logs', payload, {
    headers: sessionHeaders(),
  });
  return res.data;
}

// --- Admin (parish JWT via api interceptor) ---

export async function getCheckInPortalStatus(eventId: string): Promise<CheckInPortalStatus> {
  const res = await api.get(`/events/${eventId}/check-in-portal`);
  return res.data.data;
}

export async function createCheckInPortal(eventId: string, password: string) {
  const res = await api.post(`/events/${eventId}/check-in-portal`, { password });
  return res.data.data as { url: string; expiresAt: string };
}

export async function updateCheckInPortalPassword(eventId: string, password: string) {
  const res = await api.patch(`/events/${eventId}/check-in-portal/password`, { password });
  return res.data;
}

export async function revokeCheckInPortal(eventId: string) {
  const res = await api.delete(`/events/${eventId}/check-in-portal`);
  return res.data;
}

export async function getParishCheckInSession(eventId: string) {
  const res = await api.get(`/events/${eventId}/check-in/session`);
  const data = res.data.data;
  if (data?.sessionToken) saveCheckInSession(data.sessionToken);
  return data as {
    sessionToken: string;
    eventId: string;
    eventTitle: string;
    stats?: CheckInStats;
    customTheme?: { primaryColor?: string; secondaryColor?: string };
  };
}

export async function parishFetchCheckInStats(eventId: string): Promise<CheckInStats> {
  const res = await api.get(`/events/${eventId}/check-in/stats`);
  return res.data.data;
}

export async function parishFetchCheckInParticipants(
  eventId: string,
  params: { search?: string; filter?: 'all' | 'checked' | 'pending'; limit?: number }
) {
  const res = await api.get(`/events/${eventId}/check-in/participants`, { params });
  return res.data.data as { participants: CheckInParticipant[]; total: number };
}

export async function parishScanCheckInCode(eventId: string, code: string): Promise<CheckInScanResult> {
  const res = await api.post(`/events/${eventId}/check-in/scan`, { code });
  return res.data;
}

export async function parishManualCheckIn(eventId: string, registrationId: string): Promise<CheckInScanResult> {
  const res = await api.post(`/events/${eventId}/check-in/${registrationId}/checkin`);
  return res.data;
}

export async function parishUndoCheckIn(eventId: string, registrationId: string): Promise<CheckInScanResult> {
  const res = await api.post(`/events/${eventId}/check-in/${registrationId}/uncheckin`);
  return res.data;
}

export async function parishPostCheckInClientLogs(
  eventId: string,
  payload: {
    sessionId: string;
    eventId?: string;
    entries: Array<{
      ts: string;
      level: string;
      event: string;
      message: string;
      data?: Record<string, unknown>;
      durationMs?: number;
    }>;
    device?: Record<string, unknown>;
  }
) {
  const res = await api.post(`/events/${eventId}/check-in/client-logs`, payload);
  return res.data;
}

export async function parishFetchCheckInClientLogs(eventId: string, limit = 20) {
  const res = await api.get(`/events/${eventId}/check-in/client-logs`, { params: { limit } });
  return res.data.data as { batches: unknown[] };
}
